import { z } from 'zod';
import { Cents } from './common.js';
import { pageFields, PageInfo } from './paging.js';
import { LetterToHandOut, RewardDocument } from './my-rewards.js';

const Count = z.int().nonnegative();
/**
 * Letters to hand out's lists: every letter to hand out, those not handed out yet, those handed
 * out, and (APP-245 D4) the holder's copies of letters released straight to the employee.
 */
export const HandoutView = z.enum(['all', 'pending', 'handed', 'copies']);
export type HandoutView = z.infer<typeof HandoutView>;
export const LetterHandoutQuery = z
  .object({
    ...pageFields,
    view: HandoutView.default('all'),
  })
  .strict();
export const OverdueElectionQuery = LetterHandoutQuery.omit({ view: true });
/** APP-245 D11: how a letter reached its employee: marked handed out, or sent from the page. */
export const HandoutMethod = z.enum(['in_person', 'sent']);
export type HandoutMethod = z.infer<typeof HandoutMethod>;
export const LetterAcknowledgment = z
  .object({
    documentId: z.uuid(),
    markedAt: z.iso.datetime({ offset: true }),
    markedBy: z.object({ id: z.uuid(), name: z.string() }).strict(),
    method: HandoutMethod,
  })
  .strict();
export type LetterAcknowledgment = z.infer<typeof LetterAcknowledgment>;
export const LetterHandout = LetterToHandOut.extend({
  recipient: z.object({ id: z.uuid(), name: z.string(), eeNumber: z.string().nullable() }).strict(),
  amounts: z.object({ cashCents: Cents.nonnegative(), options: Count }).strict(),
  /**
   * APP-245 D4: `hand_out`, for the holder to hand out or send; `copy`, the holder's copy of a
   * letter released straight to the employee, to download only: a Spot award whose manager chose
   * the automatic path (part 2, `copyLine()`).
   */
  delivery: z.enum(['hand_out', 'copy']),
  /**
   * APP-245 part 2 (change 25): when the employee sees the award. `award_date` for every type but
   * Spot (or Release early); a Spot letter's employee sees it when it's handed out or sent
   * (`hand_out`, Path A) or when it was released to them (`release`, Path B, a copy here).
   */
  employeeSees: z.enum(['award_date', 'hand_out', 'release']),
  letters: z.array(
    RewardDocument.extend({
      purpose: z.literal('letter'),
      handout: LetterAcknowledgment.omit({ documentId: true }).nullable(),
      /**
       * `send` (APP-245 D9 [default]): sending it to the employee, which counts as handed out, is
       * offered once they can see the award (its award date, or released early); a Spot letter's
       * from its release to the holder, since sending it is what shows it (part 2).
       */
      actions: z.object({ markHandedOut: z.boolean(), send: z.boolean() }).strict(),
    }).strict(),
  ),
}).strict();
export type LetterHandout = z.infer<typeof LetterHandout>;
export const LetterHandouts = z
  .object({
    items: z.array(LetterHandout),
    summary: z
      .object({
        scope: z.literal('all_current_routed_letters'),
        asOf: z.iso.datetime({ offset: true }),
        total: Count,
        pending: Count,
        handedOut: Count,
        /** APP-245 D4: copies of letters released straight to the employee, not in `total`. */
        copies: Count,
      })
      .strict(),
    page: PageInfo,
  })
  .strict();
export type LetterHandouts = z.infer<typeof LetterHandouts>;
export const OverdueElection = z
  .object({
    lineId: z.uuid(),
    award: LetterToHandOut.shape.award,
    recipient: LetterHandout.shape.recipient,
    deadlineDate: z.iso.date(),
    timeZone: z.string(),
    daysLate: Count,
  })
  .strict();
export type OverdueElection = z.infer<typeof OverdueElection>;
export const OverdueElections = z
  .object({
    items: z.array(OverdueElection),
    summary: z
      .object({
        scope: z.literal('current_manager_overdue_elections'),
        asOf: z.iso.datetime({ offset: true }),
        count: Count,
        /**
         * The manager follow-up setting (`election.managerNudge`) is on. Off: the list is always
         * empty, so the page leaves the card out rather than promise rows that can't come (APP-093).
         */
        enabled: z.boolean(),
      })
      .strict(),
    page: PageInfo,
  })
  .strict();
export type OverdueElections = z.infer<typeof OverdueElections>;
export const AcknowledgeLetter = z.object({}).strict();
/**
 * APP-245 D9: send a letter to its employee, which counts as handed out, with the holder's own
 * words if they like (D13): bell and email, never on the letter.
 */
export const SendLetter = z.object({ note: z.string().trim().min(1).max(500).optional() }).strict();
export type SendLetter = z.infer<typeof SendLetter>;
/** The most letters one Download all holds (APP-245 D15); a choice of letters holds 25. */
export const MAX_HANDOUT_DOWNLOAD_ALL = 200;
export const DownloadHandoutLetters = z.union([
  z
    .object({
      documentIds: z
        .array(z.uuid())
        .min(1)
        .max(25)
        .refine((ids) => new Set(ids).size === ids.length, 'Choose each letter once.'),
    })
    .strict(),
  // APP-245 D15: every cleared letter in one view of the list, on every page.
  z.object({ every: HandoutView }).strict(),
]);
export type DownloadHandoutLetters = z.infer<typeof DownloadHandoutLetters>;
